import AuthorizationCheck from '@/components/AuthorizationCheck';
import { useContact } from '@/hooks/useContact';
import AiCopilot from '@/layout/AiCopilot/AiCopilot';
import MobileLayout from '@/pages/ISWAN_APP/layout/Layout';
import { MenuDataTyping } from '@/services/userInterface';
import { findLockedMenuByPath } from '@/util/utils';
import { CrownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { ChatwootCustomerService } from '@iyb/business-components';
import { history, useLocation, useModel } from '@umijs/max';
import { Button, ConfigProvider } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { BaseRouteUrl } from '../../config/BASEURLCONFIG';

export interface LayoutProps {
  children: React.ReactNode;
}

const useWorkareaStyle = createStyles(({ token, css }) => {
  return {
    copilotWrapper: css`
      height: calc(100vh - 64px); // 减去头部导航条高度
      display: flex;
      overflow: hidden; // 添加这行防止滚动条出现
    `,
    mainContent: css`
      flex: 1;
      min-width: 0; // 关键修复：允许内容收缩
      display: flex;
      flex-direction: column;
      overflow: auto; // 添加滚动条
    `,
    workarea: css`
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: auto; // 添加滚动条
    `,
    workareaHeader: css`
      box-sizing: border-box;
      height: 52px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 48px 0 28px;
      border-bottom: 1px solid ${token.colorBorder};
    `,
    headerTitle: css`
      font-weight: 600;
      font-size: 15px;
      color: ${token.colorText};
      display: flex;
      align-items: center;
      gap: 8px;
    `,
    headerButton: css`
      background-image: linear-gradient(78deg, #8054f2 7%, #3895da 95%);
      border-radius: 12px;
      height: 24px;
      width: 93px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      cursor: pointer;
      font-size: 12px;
      font-weight: 600;
      transition: all 0.3s;

      &:hover {
        opacity: 0.8;
      }
    `,
    workareaBody: css`
      flex: 1;
      border-radius: 16px;
      min-height: 0;
      min-width: 0; // 关键修复
      overflow: auto; // 添加滚动条
    `,
    bodyContent: css`
      height: 100%;
      min-width: 0; // 关键修复
      position: relative;
    `,
    bodyText: css`
      color: ${token.colorText};
      padding: 8px;
    `,
    lockedPageContent: css`
      height: 100%;
      min-width: 0;
      opacity: 0.62;
      filter: blur(0.8px);
      pointer-events: none;
      user-select: none;
      transition: opacity 0.2s ease;
    `,
    lockedPageMask: css`
      position: absolute;
      inset: 0;
      z-index: 20;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 28px;
      background: rgba(246, 248, 252, 0.62);
      backdrop-filter: blur(1.5px);
      -webkit-backdrop-filter: blur(1.5px);
      pointer-events: auto;
    `,
    lockedPageNotice: css`
      width: min(640px, 100%);
      min-height: 104px;
      display: flex;
      align-items: center;
      gap: 18px;
      padding: 18px 22px;
      text-align: left;
      background: rgba(255, 255, 255, 0.96);
      border: 1px solid #f2d1d1;
      border-left: 3px solid #e56b6f;
      border-radius: 8px;
      box-shadow: 0 12px 30px rgba(31, 41, 55, 0.12);

      @media (max-width: 720px) {
        flex-direction: column;
        width: min(420px, 100%);
        padding: 24px;
        text-align: center;
      }
    `,
    lockedPageIcon: css`
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #cf1322;
      font-size: 19px;
      background: #fff1f0;
      border: 1px solid #ffccc7;
      border-radius: 50%;
      flex: 0 0 auto;
    `,
    lockedPageCopy: css`
      flex: 1;
      min-width: 0;
    `,
    lockedPageVersion: css`
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 4px;
      color: #cf1322;
      font-size: 12px;
      font-weight: 600;
    `,
    lockedPageTitle: css`
      margin-bottom: 3px;
      color: ${token.colorText};
      font-size: 17px;
      font-weight: 600;
      line-height: 1.45;
    `,
    lockedPageDescription: css`
      color: ${token.colorTextSecondary};
      font-size: 13px;
      line-height: 1.5;
    `,
    lockedPageAction: css`
      width: 148px;
      height: 36px;
      font-weight: 600;
      border-radius: 6px;

      &.ant-btn-primary {
        color: #fff;
        background: #d9534f !important;
        border-color: #d9534f !important;
      }

      &.ant-btn-primary:hover,
      &.ant-btn-primary:focus {
        color: #fff;
        background: #c94743 !important;
        border-color: #c94743 !important;
      }
    `,
  };
});

const Layout: React.FC<LayoutProps> = (props) => {
  const { styles: workareaStyles } = useWorkareaStyle();

  // ==================== State =================
  const { copilotOpen, setCopilotOpen } = useModel('copilot');
  const { initialState } = useModel('@@initialState');
  const location = useLocation();
  const contactOptions = useContact();
  const currentPath = BaseRouteUrl
    ? location.pathname.replace(BaseRouteUrl, '')
    : location.pathname;
  const lockedMenu = findLockedMenuByPath(
    (initialState?.menuData as MenuDataTyping[]) || [],
    currentPath,
  );

  if (isMobile) {
    return (
      <>
        <MobileLayout>{props.children}</MobileLayout>
      </>
    );
  }

  // ==================== Render =================
  return (
    <div className="custom_container_box" style={{ padding: 0 }}>
      <ConfigProvider pagination={{ showSizeChanger: true }}>
        <div className={workareaStyles.copilotWrapper}>
          {/** 左侧工作区 */}
          <div className={workareaStyles.mainContent}>
            <div className={workareaStyles.workarea}>
              <div className={workareaStyles.workareaBody} style={{ margin: copilotOpen ? 0 : 0 }}>
                <div className={workareaStyles.bodyContent}>
                  {lockedMenu ? (
                    <>
                      <div className={workareaStyles.lockedPageContent}>{props.children}</div>
                      <div className={workareaStyles.lockedPageMask}>
                        {/*
                         * 锁定说明属于页面内容而不是自动弹窗。
                         * 用户关闭试用提醒后仍能理解菜单为何不可用，并可主动查看版本方案。
                         */}
                        <div className={workareaStyles.lockedPageNotice}>
                          <div className={workareaStyles.lockedPageIcon}>
                            <ExclamationCircleOutlined />
                          </div>
                          <div className={workareaStyles.lockedPageCopy}>
                            <div className={workareaStyles.lockedPageVersion}>
                              <CrownOutlined />
                              <span>尊享版功能</span>
                            </div>
                            <div className={workareaStyles.lockedPageTitle}>
                              {lockedMenu.name || '业务功能'}
                            </div>
                            <div className={workareaStyles.lockedPageDescription}>
                              当前版本暂未包含此功能，升级后即可使用
                            </div>
                          </div>
                          <Button
                            className={workareaStyles.lockedPageAction}
                            type="primary"
                            onClick={() => history.push('/versionTrial')}
                          >
                            查看版本方案
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    props.children
                  )}
                </div>
              </div>
            </div>
          </div>
          {/** 右侧对话区 */}
          <AiCopilot copilotOpen={copilotOpen} setCopilotOpen={setCopilotOpen} />
          {/*<AiCopilot_Demo copilotOpen={copilotOpen} setCopilotOpen={setCopilotOpen}/>*/}
        </div>
        {/** 授权检查组件 */}
        <AuthorizationCheck />
        {contactOptions ? <ChatwootCustomerService {...contactOptions} /> : null}
      </ConfigProvider>
      {/*<AIFloatButton/>*/}
    </div>
  );
};

export default Layout;
